import * as pdfjsLib from 'pdfjs-dist';
import api from '@/lib/api';

// Use CDN worker to avoid webpack bundling issues
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/**
 * Renders a PDF blob into an array of page images (data URLs).
 */
async function renderPdfPages(blob, filename) {
  const arrayBuffer = await blob.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pages = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const scale = 2;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;
    pages.push({
      filename,
      dataUrl: canvas.toDataURL('image/jpeg', 0.85),
      pageNum: i,
      totalPages: pdf.numPages,
    });
  }

  return pages;
}

/**
 * Converts an image blob to a data URL page object.
 */
async function renderImagePage(blob, filename) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve([{
        filename,
        dataUrl: reader.result,
        pageNum: 1,
        totalPages: 1,
      }]);
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Fetches all documents for a client and renders them as printable page images.
 * Returns an array of { filename, dataUrl, pageNum, totalPages }
 */
export async function prepareDocumentsForPrint(documents) {
  const allPages = [];

  for (const doc of documents) {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const blob = response.data;
      const contentType = doc.content_type || blob.type || '';

      if (contentType.includes('pdf')) {
        const pages = await renderPdfPages(blob, doc.original_filename);
        allPages.push(...pages);
      } else if (contentType.startsWith('image/')) {
        const pages = await renderImagePage(blob, doc.original_filename);
        allPages.push(...pages);
      }
      // Skip non-image/non-PDF files (Excel, CSV) - can't render inline
    } catch (err) {
      console.error(`Failed to prepare document: ${doc.original_filename}`, err);
    }
  }

  return allPages;
}
